import { OVERLAP_STATUS } from "common/constants/overlap-status";
import React from "react";

export type ResolveOutcome = {
  id: number;
  status: "resolved" | "not_found" | "failed";
  error?: string;
};

/**
 * Shown while "Double Entry" is picked, because unlike the other statuses it
 * leads on to merging the payments, which cannot be undone from the page.
 */
export const DoubleEntryMergeNotice: React.FC<{ status: string }> = ({
  status,
}) =>
  status === OVERLAP_STATUS.NOT_DUPLICATE_DOUBLE_ENTRY ? (
    <div className="alert alert-warning mt-3 mb-0" role="status">
      <i className="ri-git-merge-line align-bottom me-1" aria-hidden="true" />
      Next, you will choose which value to keep for each field. The two payments
      are then merged into one and the other record is permanently deleted.
    </div>
  ) : null;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** One toast-ready line for a resolve call, with anything that went wrong. */
export const describeResolveOutcomes = (outcomes: ResolveOutcome[]) => {
  const count = (status: ResolveOutcome["status"]) =>
    outcomes.filter((o) => o.status === status).length;
  const resolved = count("resolved");
  const failed = count("failed");
  const missing = count("not_found");

  const parts: string[] = [];
  if (resolved) parts.push(`${plural(resolved, "payment")} resolved`);
  if (missing) parts.push(`${missing} no longer exist`);

  return {
    failed,
    message: failed
      ? `${plural(failed, "payment")} could not be resolved${
          parts.length ? `; ${parts.join(", ")}` : ""
        }`
      : parts.join(", ") || "Nothing to resolve",
  };
};

/** The server's reason when it gives one, e.g. a sync already running. */
export const resolveErrorMessage = (e: any, fallback: string) =>
  e?.response?.data?.error?.message || fallback;
